// Apolo IDE — LoginPage component.
//
// Single page that toggles between Login and Bootstrap (first-run register).
// Bootstrap mode is auto-selected when /bootstrap-status returns
// `bootstrapped: false`.

import { useEffect, useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore.js';
import { getRememberMe, setRememberMe } from '../lib/api.js';
import { useApoloLogo } from '../hooks/useApoloLogo.js';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [mode, setMode] = useState('login');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [remember, setRemember] = useState(() => getRememberMe());
  const logo = useApoloLogo(128);

  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const registeredEmailHint = useAuthStore((s) => s.registeredEmailHint);
  const status = useAuthStore((s) => s.status);
  const storeError = useAuthStore((s) => s.error);
  const refreshBootstrapStatus = useAuthStore((s) => s.refreshBootstrapStatus);
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);

  useEffect(() => {
    refreshBootstrapStatus();
  }, [refreshBootstrapStatus]);

  useEffect(() => {
    if (bootstrapped === false) setMode('register');
  }, [bootstrapped]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLocalError(null);
    setSubmitting(true);
    // Persist the "remember me" preference BEFORE auth call so the resulting
    // token lands in the correct storage (localStorage vs sessionStorage).
    setRememberMe(remember);
    try {
      const result =
        mode === 'register'
          ? await register(email, password, name)
          : await login(email, password);
      if (!result.ok) setLocalError(result.error || 'Erro de autenticação');
    } finally {
      setSubmitting(false);
    }
  }

  const errorMessage = localError ?? storeError;
  const isBootstrap = mode === 'register';

  return (
    <main className="flex min-h-screen items-center justify-center bg-apolo-bg text-apolo-textPrimary px-6">
      <section
        className="w-full max-w-md rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1 p-8 shadow-2xl"
        aria-labelledby="apolo-auth-heading"
      >
        <header className="mb-6 flex flex-col items-center gap-2 text-center">
          <img
            src={logo.src}
            srcSet={logo.srcSet}
            alt=""
            aria-hidden="true"
            width={64}
            height={64}
            className="h-16 w-16 select-none object-contain"
            style={{ filter: 'var(--apolo-logo-glow-gold)' }}
            draggable={false}
          />
          <h1 id="apolo-auth-heading" className="text-2xl font-semibold tracking-tight">
            Apolo IDE
          </h1>
          <p className="text-sm text-apolo-textSecondary">
            {isBootstrap ? 'Primeiro acesso — crie sua conta' : 'Entre na sua conta'}
          </p>
          {bootstrapped && registeredEmailHint && !isBootstrap && (
            <p className="text-xs text-apolo-textTertiary">
              Conta cadastrada nesta API: <span className="font-mono text-apolo-textSecondary">{registeredEmailHint}</span>
            </p>
          )}
        </header>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          {isBootstrap && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-apolo-textSecondary">Nome</span>
              <input
                type="text"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                placeholder="Como devo te chamar?"
              />
            </label>
          )}

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-apolo-textSecondary">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
              placeholder="voce@dominio.com"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-apolo-textSecondary">Senha</span>
            <input
              type="password"
              required
              minLength={isBootstrap ? 10 : 1}
              autoComplete={isBootstrap ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
              placeholder={isBootstrap ? 'Mínimo 10 caracteres' : 'Sua senha'}
            />
          </label>

          <label className="flex items-center gap-2 text-xs text-apolo-textSecondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-apolo-border bg-apolo-surface2 text-apolo-accent focus:ring-2 focus:ring-apolo-accent/50"
            />
            <span>Manter logado neste dispositivo</span>
          </label>

          {errorMessage && (
            <div
              role="alert"
              className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-3 py-2 text-sm text-apolo-error"
            >
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || status === 'loading'}
            aria-disabled={submitting || status === 'loading'}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-apolo-accent px-4 py-2 text-sm font-medium text-apolo-bg transition hover:bg-apolo-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing disabled:cursor-not-allowed disabled:bg-apolo-surface3 disabled:text-apolo-textTertiary"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : (
              <KeyRound size={16} aria-hidden="true" />
            )}
            {isBootstrap ? 'Criar conta' : 'Entrar'}
          </button>

          {bootstrapped && (
            <button
              type="button"
              onClick={() => setMode(isBootstrap ? 'login' : 'register')}
              className="text-xs text-apolo-textTertiary hover:text-apolo-textSecondary transition"
            >
              {isBootstrap ? 'Já tem conta? Entrar' : 'Primeira vez? Criar conta'}
            </button>
          )}
        </form>
      </section>
    </main>
  );
}
